import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { format } from 'date-fns'

import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { useCreateCreatorPayout } from '@/hooks/use-mutations'
import { toast } from 'sonner'

const generatePayoutSchema = z.object({
  periodStart: z.string().min(1, 'Início do período é obrigatório'),
  periodEnd: z.string().min(1, 'Fim do período é obrigatório'),
  platformFeeRate: z.coerce.number().min(0).max(100).default(0),
})

type GeneratePayoutValues = z.infer<typeof generatePayoutSchema>

interface CreatorPayoutDialogProps {
  creatorId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Render a modal dialog that collects a period and platform fee to generate a payout (closing) for a specific creator.
 *
 * The form defaults the period to the previous calendar month and accepts a platform fee as a percentage; on submit it invokes the payout creation mutation, converts the percentage to a decimal, shows success or error toasts, closes the dialog via `onOpenChange(false)` on success, and resets the form.
 *
 * @param creatorId - Identifier of the creator for whom the payout will be generated
 * @param open - Whether the dialog is visible
 * @param onOpenChange - Callback invoked when the dialog open state changes
 * @returns The dialog element that contains the payout form and actions
 */
export function CreatorPayoutDialog({ creatorId, open, onOpenChange }: CreatorPayoutDialogProps) {
  const { mutateAsync: createPayout, isPending } = useCreateCreatorPayout()

  // Default to closing the last month
  const today = new Date()
  const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
  const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0)

  const form = useForm<GeneratePayoutValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(generatePayoutSchema) as any,
    defaultValues: {
      periodStart: format(firstDayLastMonth, 'yyyy-MM-dd'),
      periodEnd: format(lastDayLastMonth, 'yyyy-MM-dd'),
      platformFeeRate: 0,
    },
  })

  async function onSubmit(values: GeneratePayoutValues) {
    try {
      // Backend expects the rate as decimal, e.g. 0.05 for 5%
      const rateAsDecimal = values.platformFeeRate / 100

      await createPayout({
        creatorId,
        periodStart: new Date(`${values.periodStart}T00:00:00Z`).toISOString(),
        periodEnd: new Date(`${values.periodEnd}T23:59:59Z`).toISOString(),
        platformFeeRate: rateAsDecimal,
      })

      toast.success('Fechamento de comissão gerado com sucesso!')
      onOpenChange(false)
      form.reset()
    } catch (error) {
      toast.error('Erro ao gerar fechamento', {
        description: (error as Error).message || 'Verifique se há vendas no período',
      })
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Gerar Payout (Fechamento)</SheetTitle>
          <SheetDescription>
            Defina o período para apurar todas as comissões aprovadas deste afiliado.
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col flex-1 overflow-hidden"
          >
            <SheetBody className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="periodStart"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data Inicial</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="periodEnd"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data Final</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="platformFeeRate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Taxa da Plataforma (%)</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.1" placeholder="Ex: 5" {...field} />
                    </FormControl>
                    <p className="text-xs text-muted-foreground mt-1">
                      Deduzido do valor líquido a repassar caso você cobre taxa de uso.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </SheetBody>

            <SheetFooter>
              <ChonkyButton type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancelar
              </ChonkyButton>
              <ChonkyButton type="submit" loading={isPending}>
                Calcular e Gerar Payout
              </ChonkyButton>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}
